import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import CrisisSupportCard from "../components/CrisisSupportCard";
import { trackEvent } from "../lib/analytics";
import { MessageSquareText, Send, LifeBuoy, ArrowRight } from "lucide-react";

const SESSION_KEY = "zaf_try_session";
const TRANSCRIPT_KEY = "zaf_try_transcript";

function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (crypto?.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export default function TryZaf() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [gated, setGated] = useState(false);
  const [crisis, setCrisis] = useState(null);
  const scrollRef = useRef(null);
  const crisisRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    trackEvent("TryZafOpened");
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    if (crisis) crisisRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [crisis]);

  const persist = (msgs) => {
    try {
      localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(msgs.map((m) => ({ role: m.role, text: m.text }))));
    } catch (e) {}
  };

  const goSignup = () => {
    trackEvent("Lead");
    nav("/signup");
  };

  const send = async (override) => {
    const payload = (override ?? text).trim();
    if (!payload || sending || gated) return;
    if (messages.length === 0) trackEvent("TryZafFirstMessage");
    setText("");
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    const optimistic = { id: `u-${Date.now()}`, role: "user", text: payload };
    const next = [...messages, optimistic];
    setMessages(next);
    persist(next);
    setSending(true);
    try {
      const { data } = await api.post("/try-chat", { session_id: sessionId.current, text: payload, history });
      if (data.gate) {
        setGated(true);
        trackEvent("TryZafGate");
        return;
      }
      const zafMsg = { id: `z-${Date.now()}`, role: "zaf", text: data.reply };
      const withReply = [...next, zafMsg];
      setMessages(withReply);
      persist(withReply);
      if (data.crisis?.detected) setCrisis(data.crisis);
    } catch (err) {
      const zafMsg = { id: `z-${Date.now()}`, role: "zaf", text: "Sorry, I couldn't hear that just now — try me again in a sec?" };
      setMessages((prev) => [...prev, zafMsg]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = ["Just wanted to say hi", "I had a rough day", "Something good happened", "I can't sleep"];

  return (
    <div className="min-h-screen bg-[#F4F7F6] flex flex-col">
      {/* top bar */}
      <header className="bg-[#0E1417] text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="try-home-link">
            <span className="w-8 h-8 rounded-lg bg-[#2E9D96] flex items-center justify-center"><MessageSquareText size={16} /></span>
            <span className="font-grotesk font-bold text-[20px] tracking-tight">m8 im here</span>
          </Link>
          <Link to="/login" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="try-login-link">
            Log in
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 md:px-6 py-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500" data-testid="try-tagline">
            You're talking to <span className="font-semibold text-slate-700">Zaf</span> — no account needed.
          </p>
          <button
            data-testid="try-help-btn"
            onClick={() => setCrisis({ region: "", hotlines: [
              { name: "Emergency", number: "000 / 911" },
              { name: "AU Lifeline", number: "13 11 14" },
              { name: "US Lifeline", number: "988" },
            ], note: "If you're in immediate danger, please call your local emergency number now." })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F7B75] hover:text-[#2E9D96] bg-[#2E9D96]/[0.06] hover:bg-[#2E9D96]/10 border border-[#2E9D96]/25 rounded-full px-4 py-1.5 transition-colors"
          >
            <LifeBuoy size={15} /> Need help now
          </button>
        </div>

        {crisis && (
          <div ref={crisisRef}>
            <CrisisSupportCard resources={crisis} anonymous onSignup={goSignup} onDismiss={() => setCrisis(null)} />
          </div>
        )}

        <div
          ref={scrollRef}
          data-testid="try-scroll"
          className="flex-1 rounded-3xl bg-white border border-slate-100 shadow-sm p-4 md:p-6 overflow-y-auto min-h-[420px] max-h-[62vh]"
        >
          {messages.length === 0 && !sending && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="try-empty">
              <div className="w-14 h-14 rounded-full bg-[#2E9D96] text-white flex items-center justify-center font-display text-xl mb-4">Z</div>
              <h3 className="font-display text-2xl text-slate-800">Hey, I'm Zaf.</h3>
              <p className="text-slate-500 mt-1 max-w-md">No need to explain everything. Just start with whatever's on your mind.</p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-sm px-4 py-2 rounded-full bg-[#CDE7E3] hover:bg-[#B9DBD5] text-slate-700 transition-colors"
                    data-testid={`try-suggestion-${s.slice(0, 10)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-3">
            {messages.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
            {sending && (
              <div className="flex items-end gap-2 bubble-in">
                <ZafAvatar />
                <div className="bg-[#2E9D96] text-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1">
                    <span className="typing-dot inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
                    <span className="typing-dot inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
                    <span className="typing-dot inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {gated ? (
          <div className="rounded-3xl bg-white border border-[#2E9D96]/30 shadow-sm p-6 md:p-8 text-center" data-testid="try-gate">
            <div className="w-12 h-12 rounded-2xl bg-[#2E9D96] text-white flex items-center justify-center mx-auto mb-4 font-display text-xl">Z</div>
            <h3 className="font-display text-2xl text-slate-800">I'd love to keep going with you.</h3>
            <p className="text-slate-600 mt-2 max-w-md mx-auto">
              Create a free account and we'll pick up right where we left off — plus I can check in on you every day and
              gently reach out to someone you trust if you ever go quiet.
            </p>
            <button
              onClick={goSignup}
              data-testid="try-gate-signup-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#2E9D96] hover:bg-[#1F7B75] text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
            >
              Keep talking — 3 days free <ArrowRight size={17} />
            </button>
            <p className="mt-3 text-xs text-slate-400">Then about a coffee a week. No lock-in. Safety features always free.</p>
          </div>
        ) : (
          <div className="rounded-full bg-white border border-slate-200 shadow-sm flex items-center gap-2 px-2 py-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
              placeholder="Tell Zaf anything…"
              className="flex-1 bg-transparent outline-none px-3 text-slate-800 placeholder:text-slate-400"
              data-testid="try-input"
            />
            <button
              onClick={() => send()}
              disabled={sending || !text.trim()}
              className="w-11 h-11 rounded-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-40 text-white flex items-center justify-center transition-colors"
              data-testid="try-send-btn"
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function ZafAvatar() {
  return (
    <div className="w-8 h-8 rounded-full bg-[#2E9D96] text-white flex items-center justify-center text-xs font-semibold shrink-0">Z</div>
  );
}

function Bubble({ m }) {
  const isZaf = m.role === "zaf";
  return (
    <div className={`bubble-in flex items-end gap-2 ${isZaf ? "" : "justify-end"}`} data-testid={`try-msg-${m.role}`}>
      {isZaf && <ZafAvatar />}
      <div className={`max-w-[78%] rounded-2xl px-4 py-3 shadow-sm ${isZaf ? "bg-[#2E9D96] text-white rounded-bl-md" : "bg-[#CDE7E3] text-slate-800 rounded-br-md"}`}>
        {m.text && <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{m.text}</p>}
      </div>
    </div>
  );
}
